import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { queryClient } from "..";
import { ongBaseApi } from "../api/ong-base-api";
import { userApi } from "../api/user-api";
import { getMeAction } from "../app/reducers/profile-slice";
import {
  setUserPageAction,
  setUserPageByDtoAction,
} from "../app/reducers/user-slice";
import { store } from "../app/store";
import { Confirm } from "../types/confirm";
import { BEARER } from "../types/constant";
import { LocalStorageKey } from "../types/local-storage/local-storage-key";
import { SessionStorageKey } from "../types/session-storage/session-storage-key";
import {
  CreateUserPayload,
  SearchUserPagePayload,
  UpdateUserPayload,
} from "../types/user/user-payload";
import { clearLocalStorageAccessToken } from "../utils/local-storage/token";
import { clearSessionStorageAccessToken } from "../utils/session-storage/token";
import {endGlobalLoadingAction} from "../app/reducers/loading-slice";

export enum UserQueryKey {
  Create = "CreateUser",
  CreateBulk = "CreateUserBulk",
  Update = "UpdateUser",
  Delete = "DeleteUser",
  GetById = "GetUserById",
  Page = "UserPage",
  GetMe = "GetMe",
}

export const useCreateUserMutation = () => {
  return useMutation(
    UserQueryKey.Create,
    (payload: CreateUserPayload) => userApi.create(payload),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(UserQueryKey.GetById);
        toast.success(Confirm.Confirm);
      },
    }
  );
};

export const useCreateUserBulkMutation = () => {
  return useMutation(
    UserQueryKey.CreateBulk,
    (payload: CreateUserPayload[]) => userApi.createBulk(payload),
  );
};

export const useUpdateUserMutation = () => {
  return useMutation(
    UserQueryKey.Update,
    (params: { id: string; payload: UpdateUserPayload }) => {
      return userApi.update(params.id, params.payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(UserQueryKey.GetById);
        toast.success(Confirm.Update);
      },
    }
  );
};

export const useDeleteUserMutation = () => {
  return useMutation(UserQueryKey.Delete, (id: string) => userApi.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(UserQueryKey.Page);
      toast.success(Confirm.Delete);
    },
  });
};

export const useGetUserByIdQuery = (payload: string) => {
  return useQuery(
    [UserQueryKey.GetById, payload],
    () => userApi.getById(payload),
    {
      enabled: !!payload,
      onSuccess: ({ data }) => {
        const userDto = data;
        store.dispatch(
          setUserPageByDtoAction({
            userDto,
          })
        );
      },
    }
  );
};

export const useGetUserPageQuery = (payload: SearchUserPagePayload) => {
  return useQuery([UserQueryKey.Page, payload], () => userApi.search(payload), {
    enabled: payload.hasOwnProperty("page"),
    onSuccess: ({ data }) => {
      const userPageDto = data;
      store.dispatch(
        setUserPageAction({
          userPageDto,
        })
      );
      store.dispatch(endGlobalLoadingAction());
    },
  });
};

export const useGetMeQuery = () => {
  const accessToken =
    localStorage.getItem(LocalStorageKey.AccessToken) ||
    sessionStorage.getItem(SessionStorageKey.AccessToken);

  if (accessToken) {
    const bearer = BEARER + accessToken;
    ongBaseApi.defaults.headers.common["Authorization"] = bearer;
  }

  return useQuery([UserQueryKey.GetMe], () => userApi.getMe(), {
    enabled: !!accessToken,
    onSuccess: ({ data }) => {
      const userDto = data;
      store.dispatch(getMeAction({ userDto }));
    },
    onError: (error) => {
      console.log("useGetMeQuery onError", error);
      clearLocalStorageAccessToken();
      clearSessionStorageAccessToken();
      delete ongBaseApi.defaults.headers.common["Authorization"];
    },
  });
};
